import React, { Component, PropTypes } from 'react';
import { PropTypes as MobxPropTypes } from 'mobx-react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';

// data dependencies
import KPIGridLayoutUIStore from './Izenda.KPIGridLayoutUIStore';
import { draggableClass, TileTypes } from 'logic/data/Izenda.ReportPartKPIConstants.js';
import { REPORT_PART_MODE } from 'logic/data/Izenda.ReportBodyConstants';
// share dependencies
import connect from 'share/enhancers/Izenda.Connect.js'
import GridItem from 'share/components/controls/GridLayout/Izenda.GridItem';
import GridView from 'share/components/controls/GridLayout/Izenda.GridView';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';


// module imports
import TilePropertiesSchema from 'modules/ReportDesigner/TilePropertiesContainer/izenda.TilePropertiesContainerSchema.js';
import ReportPartTitle from '../FieldContainers/Izenda.ReportPartTitle';
// local dependencies
import KPIGridItemContainer from './Izenda.KPIGridItemContainer';



const TILE_PROPERTIES_EDITOR = [{
    name: 'tileProps',
    getDefinedSchema: TilePropertiesSchema
}];

function toKPILayoutItem(item) {
    return {
        id: item.id,
        x: item.left,
        y: item.top,
        width: item.width,
        height: item.height,
        isFullSize: false,
        autoSize: item.autoSize,
        isLoading: false
    };
}

@connect()
class ReportPartKPIGrid extends Component {
    constructor(props) {
        super(props);
        const { reportPart: { reportPartContent } } = props;
        this.layoutStore = new KPIGridLayoutUIStore({
            numRows: reportPartContent.getReportPartProperties('numRows'),
            numCols: reportPartContent.getReportPartProperties('numCols'),
            cellSize: reportPartContent.getReportPartProperties('cellSize')
        });
        this.checkUpdateTransparent();
    }

    /**
     * @method componentDidMount
     * @description create a autorun reaction that updates the layout store dimensions when
     * the kpi grid dimensions are updated
     */
    componentDidMount() {
        this.updateGridDimensionsAutoRun = autorun(() => {
            const { reportPart: { reportPartContent } } = this.props;
            const numRows = reportPartContent.getReportPartProperties('numRows');
            const numCols = reportPartContent.getReportPartProperties('numCols');

            if (reportPartContent.needUpdateLayoutStore) {
                this.layoutStore.updateGridDimensions({
                    numRows,
                    numCols,
                    cellSize: reportPartContent.getReportPartProperties('cellSize')
                });
                reportPartContent.setNeedUpdateLayoutStore(false);
            } else if (this.layoutStore.rows !== numRows || this.layoutStore.cols !== numCols) {
                this.layoutStore.updateGridDimensions({
                    numRows,
                    numCols
                });
            }

            if (reportPartContent.reportWidth !== this.layoutStore.width || reportPartContent.reportHeight !== this.layoutStore.height) {
                reportPartContent.setDimensions(this.layoutStore.width, this.layoutStore.height);
            }
        });
        this.props.reportPart.reportPartContent.checkAdditionalGridDimensions(this.layoutStore.maxItemRows, this.layoutStore.maxItemCols);
    }

    /**
     * @method componentDidUpdate
     * @description call function to check if a background image has been added or removed
     */
    componentDidUpdate() {
        this.checkUpdateTransparent();
    }

    /**
     * @method checkUpdateTransparent
     * @description set layout grid state to true or false based on background image prop, set kpi grid dimensions
     */
    checkUpdateTransparent() {
        const {
            reportPart: { reportPartContent }
        } = this.props;

        const backgroundImageProp = reportPartContent.getReportPartProperties('backgroundImage');
        if (!this.layoutStore.transparent && backgroundImageProp.dataUrl) {
            this.layoutStore.setTransparent(true);
        } else if (this.layoutStore.transparent && !backgroundImageProp.dataUrl) {
            this.layoutStore.setTransparent(false);
        }
    }

    /**
     * @method componentWillUnmount
     * @description dispose of the update grid autorun function
     */
    componentWillUnmount() {
        this.updateGridDimensionsAutoRun();
    }

    /**
     * @method updateActiveGridItem
     * @description set active grid item coordinates in layout store for use when adding grid item
     * @param {object} offset contains x and y positions cell that we are adding an item to
     */
    updateActiveGridItem = (offset) => {
        this.layoutStore.setActiveGridItem(offset);
    }

    /**
     * @method addNewGridItem
     * @description add a new grid item to the kpi content store
     * @param {string} typeId type of item that we will be adding
     */
    addNewGridItem = (typeId) => {
        this.props.reportPart.reportPartContent.addGridData(typeId, this.layoutStore.activeGridItem, true);
    }

    /**
     * @method updateItemPositions
     * @description set new position of grid item in store
     * @param {Array} newItemPos list of objects containing id and new position of moved grid item
     */
    updateItemPositions = (newItemPos) => {
        const { reportPart: { reportPartContent } } = this.props;
        newItemPos.map(pos => { reportPartContent.updateGridDataPosition(pos); });
        reportPartContent.checkAdditionalGridDimensions(this.layoutStore.maxItemRows, this.layoutStore.maxItemCols);
    }

    setActive = (item) => {
        this.props.reportPart.reportPartContent.setActiveTile(item);
    }

    /**
     * @method renderGridItemMenu
     * @description render the context menu that appears when a user attempts to add an item to the grid
     */
    renderGridItemMenu = () => {
        const {
            reportPart: { reportPartContent }
        } = this.props;

        let types = Object.values(TileTypes);
        if (reportPartContent.hasMetricTile) {
            types = types.filter(type => type.id !== TileTypes.metric.id);
        }

        return (
            <div className="grid-item-menu">
                {
                    types.map((type, index) => {
                        return (
                            <button
                                className="btn grid-item-menu__btn"
                                key={index}
                                onClick={this.addNewGridItem.bind(this, type.id)}
                            >
                                {type.menuLabel}
                            </button>
                        );
                    })
                }
            </div>
        );
    }

    /**
     * @method renderGridItems
     * @description render a grid item container for each grid data element
     */
    renderGridItems = () => {
        return this.props.reportPart.reportPartContent.gridData.map(item => {
            return (
                <GridItem
                    key={item.id}
                    id={item.id}
                    isDraggable={true}
                    isResizable={true}
                    handle={`.${draggableClass}`}
                    isKPILayout={this.layoutStore.isKPILayout}
                >
                    <KPIGridItemContainer
                        propsEditors={TILE_PROPERTIES_EDITOR}
                        tile={item}
                        {...this.props}
                    />
                </GridItem>
            );
        });
    }

    render() {
        const {
            hideTitle,
            reportPart: reportPartDetails,
            reportPart: {
                isLoadingData,
                reportPartContent
            },
            printingMode
        } = this.props;
        const layoutData = reportPartContent.gridData.map(toKPILayoutItem);
        const containerStyle = {
            backgroundImage: `url(${reportPartContent.getReportPartProperties('backgroundImage').dataUrl})`,
            backgroundRepeat: 'no-repeat',
            width: `${this.layoutStore.width}px`
        };

        const printingStyles = { ...printingMode ? PrintingUtils.getReportPartContainerStyle(this.props) : {} }
        const additionalClasses = printingMode ? 'report-part-chart-front-side' : '';
        return (
            <div className={`${additionalClasses} iz-kpi izenda-KPIFrontSide`} style={printingStyles}>
                {!hideTitle && (
                    <ReportPartTitle
                        mode={REPORT_PART_MODE.PREVIEW}
                        reportPart={reportPartDetails}
                        reportPartContent={reportPartContent}
                    />
                )}
                <div style={containerStyle}>
                    <GridView
                        allowShowHorizontalScroll={true}
                        allowAddInline={this.props.isPreview}
                        gridItemMenu={this.renderGridItemMenu()}
                        layoutStore={this.layoutStore}
                        layoutData={layoutData}
                        editable={this.props.isPreview}
                        showLoading={true}
                        printingMode={false}
                        onAddItem={this.updateActiveGridItem}
                        onUpdateItemPositions={this.updateItemPositions}
                    >
                        {this.renderGridItems()}
                    </GridView>
                </div>
            </div>
        );
    }
}

ReportPartKPIGrid.propTypes = {
    reportPart: MobxPropTypes.observableObject
};

export default ReportPartKPIGrid;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartKPI/Izenda.ReportPartKPIGrid.js